import React from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Button, Input } from "@/components/ui";
import { InviteData, AcceptInviteFields } from "./types";
import { Spinner } from "@/assets/svg/Spinner";
import { InputWrapper } from "@/components/FormInputs/InputWrapper";

export function AcceptForm({
  invite,
  onSubmit,
}: {
  invite?: InviteData;
  onSubmit: (data: AcceptInviteFields) => void;
}) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<AcceptInviteFields>({
    mode: "onChange",
    defaultValues: {
      firstName: invite?.first_name,
      lastName: invite?.last_name,
    },
  });

  const passwordsMatch = (_: string, fields: AcceptInviteFields) =>
    fields.password === fields.passwordConfirm ||
    t("acceptInvitation.errors.passwordMismatch");

  return (
    <main role="main">
      <h2 className="mb-3 text-2xl font-medium text-foreground">
        {t("acceptInvitation.title", {
          organization: invite?.organization?.name,
        })}
      </h2>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <InputWrapper
          label={t("acceptInvitation.firstName")}
          name="firstName"
          error={errors.firstName}
        >
          <Input
            {...register("firstName", {
              required: {
                value: true,
                message: t("acceptInvitation.errors.required"),
              },
            })}
          />
        </InputWrapper>
        <InputWrapper
          label={t("acceptInvitation.lastName")}
          name="lastName"
          error={errors.lastName}
        >
          <Input
            {...register("lastName", {
              required: {
                value: true,
                message: t("acceptInvitation.errors.required"),
              },
            })}
          />
        </InputWrapper>
        <InputWrapper label={t("acceptInvitation.email")}>
          <Input disabled={true} name="email" value={invite?.email} />
        </InputWrapper>
        <InputWrapper label={t("acceptInvitation.phoneNumber")}>
          <Input
            disabled={true}
            name="phoneNumber"
            value={invite?.phone_number}
          />
        </InputWrapper>
        <InputWrapper
          label={t("acceptInvitation.password.label")}
          name="password"
        >
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Input
                type="password"
                {...register("password", {
                  required: {
                    value: true,
                    message: t("acceptInvitation.errors.required"),
                  },
                })}
                placeholder={t("acceptInvitation.password.enterPassword")}
                data-testid="password-input"
              />
              {errors.password && (
                <small className="text-xs font-medium text-red-700">
                  {errors.password.message}
                </small>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Input
                type="password"
                {...register("passwordConfirm", {
                  required: {
                    value: true,
                    message: t("acceptInvitation.errors.required"),
                  },
                  validate: { passwordsMatch },
                })}
                placeholder={t("acceptInvitation.password.passwordConfirm")}
                data-testid="password-confirm-input"
              />
              {errors.passwordConfirm && (
                <small className="text-xs font-medium text-red-700">
                  {errors.passwordConfirm.message}
                </small>
              )}
            </div>
          </div>
        </InputWrapper>
        <Button type="submit" disabled={!isValid || isSubmitting}>
          {isSubmitting ? (
            <div className="flex gap-1">
              <Spinner className="text-primary/50" />
              {t("global.submitting")}
            </div>
          ) : (
            t("global.submit")
          )}
        </Button>
      </form>
    </main>
  );
}
